import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitDiff, Scales, Trash, CaretDown, CaretRight, Trophy } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadarOverview, CostBreakdown, MetricChart, VersusChart, PROPERTY_METRICS, ECONOMIC_METRICS, ALL_METRICS } from "@/pages/Compare";

function RenderProcesses({ r }) {
  return (
    <div className="pt-2">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6"><RadarOverview data={r.data} /><CostBreakdown data={r.data} /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">{PROPERTY_METRICS.map((m) => <MetricChart key={m.key} metric={m} data={r.data} />)}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">{ECONOMIC_METRICS.map((m) => <MetricChart key={m.key} metric={m} data={r.data} />)}</div>
      {r.best_process && <div className="flex items-start gap-3 border border-primary/40 bg-primary/10 p-4 rounded-sm"><Trophy size={20} weight="fill" className="text-primary shrink-0 mt-0.5" /><p className="text-sm text-foreground">{r.summary}</p></div>}
    </div>
  );
}

function RenderMaterials({ r }) {
  return (
    <div className="pt-2">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">{ALL_METRICS.map((m) => <VersusChart key={m.key} metric={m} mats={r.materials} />)}</div>
      {r.verdict && <div className="flex items-start gap-3 border border-secondary/40 bg-secondary/10 p-4 rounded-sm"><Trophy size={20} weight="fill" className="text-secondary shrink-0 mt-0.5" /><p className="text-sm text-foreground">{r.verdict}</p></div>}
    </div>
  );
}

export default function SavedComparisons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems((await api.get("/comparisons")).data); } catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/comparisons/${delTarget.id}`);
      toast.success("Deleted");
      setItems((p) => p.filter((x) => x.id !== delTarget.id));
      setDelTarget(null);
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="grain min-h-[50vh] flex items-center justify-center font-mono text-sm text-muted-foreground">loading<span className="cursor-blink">_</span></div>;

  return (
    <div className="grain mx-auto max-w-[1200px] px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <GitDiff size={26} weight="bold" className="text-primary" />
        <div>
          <h1 className="font-heading font-black text-white text-2xl">My Saved Comparisons</h1>
          <p className="text-sm text-muted-foreground">{items.length} saved.</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-border bg-card p-12 text-center">
          <p className="font-heading text-white text-lg">Nothing saved yet</p>
          <p className="text-sm text-muted-foreground mt-1">Run a comparison and hit Save to keep it here.</p>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {items.map((it) => {
            const open = openId === it.id;
            return (
              <div key={it.id} data-testid={`scmp-${it.id}`} className="bg-card">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button data-testid={`scmp-toggle-${it.id}`} onClick={() => setOpenId(open ? null : it.id)} className="flex items-center gap-3 flex-1 text-left">
                    {open ? <CaretDown size={16} className="text-primary" /> : <CaretRight size={16} className="text-muted-foreground" />}
                    <span className={`shrink-0 border px-2 py-0.5 text-[10px] font-mono uppercase rounded-sm ${it.kind === "processes" ? "text-primary border-primary/40 bg-primary/10" : "text-secondary border-secondary/40 bg-secondary/10"}`}>
                      {it.kind === "processes" ? <GitDiff size={11} className="inline mr-1" /> : <Scales size={11} className="inline mr-1" />}{it.kind}
                    </span>
                    <span className="text-white text-sm truncate">{it.title}</span>
                    <span className="ml-auto font-mono text-[10px] text-muted-foreground hidden sm:block">{new Date(it.created_at).toLocaleDateString()}</span>
                  </button>
                  <Button data-testid={`scmp-delete-${it.id}`} onClick={() => setDelTarget(it)} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive shrink-0"><Trash size={16} /></Button>
                </div>
                {open && (
                  <div className="px-4 pb-6 border-t border-border">
                    {it.kind === "processes" ? <RenderProcesses r={it.payload} /> : <RenderMaterials r={it.payload} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-sm bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-white">Delete saved comparison?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="scmp-confirm-delete" onClick={confirmDelete} className="rounded-sm bg-destructive hover:bg-destructive/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
